import React from "react";
//#region types
import { ReportNavigationContentProps, ReportNavSection } from "models/propTypes/reportsPropTypes";
import { IconDefinitionConstants, ReportConstants } from "utils/constants";
import { StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import ReportList from "./reportList";
import ReportEmptyState from "./reportEmptyState";
import { useCheckRelations, useSearchContext } from "components/common";
import {
  useQualifiedShareDividendsService,
  useAgreementService,
  useTimeReportingProjectService,
  useBusinessUnitService,
} from "serviceHooks";
import { useIncomingInvoicePermissions } from "components/incomingInvoice";
import { useSharedDocumentPermissions, useGenericDocumentPermissions } from "components/documents";
import { useHoldingRegisterPermissions } from "components/advisor";
import { useInvoicePermissions } from "components/invoice";
import { useQuotationPermissions } from "components/quotation";
import { useReceiptPermissions } from "components/receipt";
import { useSIEFilesPermissions } from "components/documents";
import { useFinancialReportPermissions } from "components/accounting";
import { useCashReconciliationPermissions } from "components/cashReconciliation";
import { useAnnualAccountPermissions } from "components/annualAccount";
//#endregion
import { CompanyService } from "services";
import { LocalizationHelperInstance } from "utils/helpers";

const ReportNavigationContent: React.FC<ReportNavigationContentProps> = (props) => {
  const timeReportingProjectService = useTimeReportingProjectService();
  const agreementService = useAgreementService();
  const qualifiedShareDividendsService = useQualifiedShareDividendsService();
  const financialReportPermissions = useFinancialReportPermissions();
  const businessUnitService = useBusinessUnitService();

  const { setNavLayoutSections } = props;

  const searchContext = useSearchContext();

  const { getIsAdmin } = useCheckRelations();
  const { getIsBoard } = useCheckRelations();
  const isAdmin = getIsAdmin();
  const { canUseInvoices, canUseRecurringInvoices } = useInvoicePermissions();
  const { canViewQuotations } = useQuotationPermissions();
  const { canViewGenericDocuments } = useGenericDocumentPermissions();
  const { canViewIncomingInvoices } = useIncomingInvoicePermissions();
  const { hasSharedDocumentViewPermissions } = useSharedDocumentPermissions();
  const { hasHoldingRegisterAvailable } = useHoldingRegisterPermissions();
  const { receiptViewer } = useReceiptPermissions();
  const { canViewCashReconciliations } = useCashReconciliationPermissions();
  const cashReconciliationsVisible = !!canViewCashReconciliations();
  const { hasSieFileViewPermissions } = useSIEFilesPermissions();
  const { getHasFinancialStatementPermissions } = useAnnualAccountPermissions();

  const reports: ReportNavSection[] = [
    {
      key: ReportConstants.NavKeys.Income,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.Income),
      icon: IconDefinitionConstants.WIncome,
      displayType: StyleEnums.DisplayType.Info,
      reports: [
        {
          reportKey: "CustomerInvoices",
          icon: IconDefinitionConstants.WSend,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.CustomerInvoices),
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.InvoicesDescription),
          reportUrl: "/Reports/Revenue/Invoices",
          hidden: !canUseInvoices,
        },
        {
          reportKey: "CustomerQuotes",
          icon: IconDefinitionConstants.WCreate,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Quotes),
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Reports.CustomerQuotesDescription
          ),
          reportUrl: "/Reports/Quotes",
          hidden: !canViewQuotations,
        },
        {
          reportKey: "RecurringInvoices",
          icon: IconDefinitionConstants.WRecurring,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.RecurringInvoices),
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Reports.RecurringInvoicesDescription
          ),
          reportUrl: "/Reports/Revenue/RecurringInvoices",
          hidden: !canUseRecurringInvoices,
        },
        {
          reportKey: "Customers",
          icon: IconDefinitionConstants.WCustomer,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Customer.Customers),
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.CustomersDescription),
          reportUrl: "/Reports/Customers",
          hidden: !canUseInvoices,
        },
        {
          reportKey: "Articles",
          icon: IconDefinitionConstants.WBox,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Articles),
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.ArticlesDescription),
          reportUrl: "/Reports/Articles",
          hidden: !canUseInvoices,
        },
        {
          reportKey: "CashReconciliations",
          icon: IconDefinitionConstants.WCalculator,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.CashReconciliation.CashReconciliations),
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Reports.CashReconciliationListDescription
          ),
          reportUrl: "/Reports/Revenue/CashReconciliations",
          hidden: !cashReconciliationsVisible,
        },
      ],
    },
    {
      key: ReportConstants.NavKeys.Costs,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.Costs),
      icon: IconDefinitionConstants.WMoney,
      displayType: StyleEnums.DisplayType.Info,
      reports: [
        {
          reportKey: "IncomingInvoices",
          icon: IconDefinitionConstants.WSupplierInvoice,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.IncomingInvoices),
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Reports.IncomingInvoicesDescription
          ),
          reportUrl: "/Reports/Expenses/IncomingInvoices",
          hidden: !canViewIncomingInvoices,
        },
        {
          reportKey: "Suppliers",
          icon: IconDefinitionConstants.WSupplier,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.Suppliers),
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.SuppliersDescription),
          reportUrl: "/Reports/Suppliers",
          hidden: !canViewIncomingInvoices,
        },
        {
          reportKey: "Receipts",
          icon: IconDefinitionConstants.WReceipt,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.Receipts),
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.ReceiptsDescription),
          reportUrl: "/Reports/Expenses/Receipts",
          hidden: !receiptViewer,
        },
      ],
    },
    {
      key: ReportConstants.NavKeys.Analysis,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.Analysis),
      icon: IconDefinitionConstants.WReport1,
      displayType: StyleEnums.DisplayType.Info,
      reports: [
        {
          reportKey: "IncomeStatement",
          icon: IconDefinitionConstants.WReports,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.IncomeStatement),
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Reports.IncomeStatementDescription
          ),
          reportUrl: "/Reports/IncomeStatement",
          hidden: !financialReportPermissions.hasPermissions(),
        },
        {
          reportKey: "BalanceSheet",
          icon: IconDefinitionConstants.WReports,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.BalanceSheet),
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Reports.BalanceStatementDescription
          ),
          reportUrl: "/Reports/BalanceSheet",
          hidden: !financialReportPermissions.hasPermissions(),
        },
        {
          reportKey: "Accounting",
          icon: IconDefinitionConstants.WTransactions,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Accounting),
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.CompanyBooksDescription),
          reportUrl: "/Reports/Accounting",
          hidden: !financialReportPermissions.hasPermissions(),
        },
        {
          reportKey: "HoldingRegisters",
          icon: IconDefinitionConstants.WCategory,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.HoldingRegister.HoldingRegister),
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.HoldingRegister.HoldingRegisterDescription
          ),
          reportUrl: "/Reports/HoldingRegisters",
          hidden: !hasHoldingRegisterAvailable,
        },
      ],
    },
    {
      key: ReportConstants.NavKeys.Documents,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.Documents),
      icon: IconDefinitionConstants.WDocument,
      displayType: StyleEnums.DisplayType.Info,
      reports: [
        {
          reportKey: "DocumentDrafts",
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.DocumentDraft.ReceivedDocuments),
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Reports.DocumentDraftsDescription
          ),
          reportUrl: "/Reports/DocumentDrafts",
        },
        {
          reportKey: "SIEFiles",
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Document.SIEFiles),
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.SIEFilesDescription),
          reportUrl: "/Reports/SIE",
          hidden: !hasSieFileViewPermissions(),
        },
        {
          reportKey: "SharedDocuments",
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Document.SharedDocuments),
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Reports.SharedDocumentsDescription
          ),
          reportUrl: "/Reports/SharedDocuments",
          hidden: !hasSharedDocumentViewPermissions(),
        },
        {
          reportKey: "AccountingDocuments",
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Document.GenericDocuments),
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Document.GenericDocumentsDescription
          ),
          reportUrl: "/Reports/AccountingDocuments",
          hidden: !canViewGenericDocuments,
        },
        {
          reportKey: "Agreements",
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Agreement.Agreements),
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Agreement.AgreementDescription),
          reportUrl: "/Reports/Agreements",
          hidden: !agreementService.hasAgreementPermissions(),
        },
        {
          reportKey: "FinancialStatements",
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.FinancialStatements),
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.AnnualAccount.FinancialStatementDescription
          ),
          reportUrl: "/Reports/FinancialStatements",
          hidden: !getHasFinancialStatementPermissions(),
        },
        {
          reportKey: "QualifiedShareDividend",
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.K10Forms),
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.QualifiedShareDividends.ReportDescription
          ),
          reportUrl: "/Reports/QualifiedShareDividend",
          hidden: !qualifiedShareDividendsService.hasQualifiedShareDividendReportPermissions(),
        },
        {
          reportKey: "ExportedFilesView",
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ExportedFiles),
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ExportedFilesDescription),
          reportUrl: "/Reports/ExportedFilesView",
        },
      ],
    },
    {
      key: ReportConstants.NavKeys.Employees,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.Employees),
      icon: IconDefinitionConstants.WUser,
    },
    {
      key: ReportConstants.NavKeys.Administration,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.Administration),
      icon: IconDefinitionConstants.WCompany,
      displayType: StyleEnums.DisplayType.Info,
      reports: [
        {
          reportKey: "AllToDos",
          icon: IconDefinitionConstants.WTodo,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Todo),
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.TodoListDescription),
          reportUrl: "/Reports/AllToDos",
        },
        {
          reportKey: "Board",
          icon: IconDefinitionConstants.WCompany,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.Board),
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Reports.ListOfBoardMembersDescription
          ),
          reportUrl: "/Reports/Board",
          hidden: !(isAdmin || getIsBoard()),
        },
        {
          reportKey: "Automations",
          icon: IconDefinitionConstants.WBolt,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.AutomationReport),
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Reports.AutomationReportDescription
          ),
          reportUrl: "/Reports/Automations",
          hidden: !receiptViewer && !canViewIncomingInvoices,
        },
      ],
    },
    {
      key: ReportConstants.NavKeys.TimeReporting,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.TimeReporting),
      icon: IconDefinitionConstants.WTimer,
      displayType: StyleEnums.DisplayType.Info,
      reports: [
        {
          reportKey: "TimeReportingBusinessUnits",
          icon: IconDefinitionConstants.WBuilding,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.BusinessUnit.BusinessUnits),
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.BusinessUnitsDescription),
          reportUrl: "/Reports/BusinessUnits",
          hidden: !(CompanyService.isTimeReportingEnabled() && businessUnitService.hasEditPermissions()),
        },
        {
          reportKey: "Projects",
          icon: IconDefinitionConstants.WFolder,
          title: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.TimeReportingProject.TimeReportingProjects
          ),
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Reports.TimeReportingProjectsDescription
          ),
          reportUrl: "/Reports/Projects",
          hidden: !(CompanyService.isTimeReportingEnabled() && timeReportingProjectService.hasEditPermissions()),
        },
      ],
    },
    {
      key: ReportConstants.NavKeys.BusinessUnits,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.BusinessUnit.BusinessUnits),
      icon: IconDefinitionConstants.WTimer,
      displayType: StyleEnums.DisplayType.Info,
      reports: [
        {
          reportKey: "BusinessUnits",
          icon: IconDefinitionConstants.WBuilding,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.BusinessUnit.BusinessUnits),
          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.BusinessUnitsDescription),
          reportUrl: "/Reports/BusinessUnits",
          hidden: !(
            businessUnitService.isBusinessUnitsEnabled() &&
            businessUnitService.hasEditPermissions() &&
            !CompanyService.isTimeReportingEnabled()
          ),
        },
      ],
    },
  ];

  const [currentReports, setCurrentReports] = React.useState<ReportNavSection[]>();

  React.useEffect(() => {
    const filteredReports = filterReports();

    setNavLayoutSections &&
      setNavLayoutSections(
        filteredReports.map((r) => ({
          key: r.key,
          label: r.title,
          icon: r.icon,
        }))
      );
  }, [cashReconciliationsVisible]);

  React.useEffect(() => {
    filterReports(searchContext?.searchText);
  }, [searchContext?.searchText, cashReconciliationsVisible]);

  const filterReports = (searchText?: string) => {
    let filteredReports: ReportNavSection[];
    if (searchText) {
      filteredReports = reports.map((r) => ({
        ...r,
        reports: r.reports?.filter(
          (s) =>
            !s.hidden &&
            `${s.title} | ${s.description} | ${s.tags?.join(",")}`
              .toLowerCase()
              .indexOf(searchText?.toLowerCase() ?? "") !== -1
        ),
      }));
    } else {
      filteredReports = reports.map((r) => ({
        ...r,
        reports: r.reports?.filter((s) => !s.hidden),
      }));
    }

    filteredReports = filteredReports?.filter((r) => r.reports && r.reports.length > 0);
    setCurrentReports(filteredReports);
    return filteredReports;
  };

  return (
    <>
      {currentReports?.length === 0 ? (
        <ReportEmptyState />
      ) : (
        currentReports?.map((report) => (
          <ReportList
            key={report.key}
            title={report.title}
            reportKey={report.key}
            icon={report.icon}
            reports={report.reports}
            displayType={report.displayType}
          />
        ))
      )}
    </>
  );
};

export default ReportNavigationContent;
